'use client';

import clsx from 'clsx';
import Image from 'next/image';

import { Link } from '@/lib/i18n/navigation';
import { ensureTrailingSlash } from '@/lib/utils/ensureTrailingSlash';

import { useHandleQuickLink, type UrlTriggerEvent } from '../../hooks/UseNavigation';
// TODO: Import Icon when icons are added to the library
// import { Icon } from '@/components/ui/Icon';
import type { NavQuickLinkMedia } from '../NavQuickLinkItem';

import styles from './NavQuickLinkPillItem.module.scss';

/**
 * =============================================================================
 * ICON MAPPING - STUB
 * =============================================================================
 *
 * Same as NavQuickLinkItem - maps icon identifiers to icon components.
 * See NavQuickLinkItem.tsx for TODO details on adding these icons.
 */
const ICON_MAPPING: Record<string, string> = {
  shipping: 'shipping',
  gift: 'gift',
  'free-return': 'return',
  'easy-contact': 'concierge',
};

export type NavQuickLinkPillShape = 'pill' | 'rectangle';

export interface NavQuickLinkPillItemProps {
  /** Visual shape — pill (default) or rectangle */
  shape?: NavQuickLinkPillShape;
  /** System ID */
  sys?: { id: string };
  /** Icon identifier (maps to ICON_MAPPING) */
  icon?: string | null;
  /** Media asset (image) */
  media?: NavQuickLinkMedia | null;
  /** Whether this link is currently active */
  isCurrentLink?: boolean;
  /** Navigation URL */
  url?: string | null;
  /** URL target (_self, _blank) */
  urlTarget?: string | null;
  /** Display text */
  urlText?: string | null;
  /** URL trigger event (opens modal instead of navigating) */
  urlTrigger?: UrlTriggerEvent | null;
  /** Optional className */
  className?: string;
  /** Test ID */
  'data-testid'?: string;
}

function isExternalUrl(url: string, urlTarget?: string | null): boolean {
  return url.startsWith('http://') || url.startsWith('https://') || urlTarget === '_blank';
}

/**
 * NavQuickLinkPillItem - Pill-shaped navigation link
 *
 * A compact pill/chip style link commonly used for category filtering
 * or tab-like navigation. Supports active state highlighting.
 *
 * Features:
 * - Pill/chip shape with border radius
 * - Active state with inverted colors
 * - Optional icon or image
 * - Uppercase label text
 */
export function NavQuickLinkPillItem({
  shape = 'pill',
  className,
  icon,
  isCurrentLink = false,
  media,
  url,
  urlTarget,
  urlText,
  urlTrigger,
  'data-testid': testId,
}: Readonly<NavQuickLinkPillItemProps>) {
  const { handleQuickLink } = useHandleQuickLink(url ?? '/', urlTarget ?? '_self', urlTrigger);

  const hasMatchingIcon = icon && icon in ICON_MAPPING;
  const showIcon = shape !== 'rectangle' && Boolean(icon && hasMatchingIcon);
  const mediaUrl = shape !== 'rectangle' ? media?.url : null;
  const href = url ?? '/';
  const linkClassName = clsx(
    styles.wrapper,
    shape === 'rectangle' && styles.rectangle,
    isCurrentLink && styles.currentLink,
    className
  );
  const ariaCurrent = isCurrentLink ? 'page' : undefined;
  const resolvedTestId = testId ?? 'NavQuickLinkPillItemWrapper';

  const content = (
    <>
      {showIcon && (
        <span className={styles.iconWrapper}>
          {/*
            TODO: Replace with actual Icon component when icons are added
            <Icon name={ICON_MAPPING[icon]} />
            
            Icons needed: shipping, gift, free-return, easy-contact
          */}
          <span aria-hidden="true">●</span>
        </span>
      )}

      {mediaUrl && (
        <span className={styles.imgBox} data-testid="NavQuickLinkPillItemNavQuickImgBox">
          <Image
            src={mediaUrl}
            alt={media?.title ?? ''}
            fill
            sizes="24px"
            className={styles.img}
            aria-hidden
            data-testid="NavQuickLinkPillItemNavQuickImg"
          />
        </span>
      )}

      <span className={styles.label} data-testid="NavQuickLinkPillItemLabel">
        {urlText}
      </span>
    </>
  );

  if (urlTrigger?.type) {
    return (
      <button
        className={linkClassName}
        onClick={handleQuickLink}
        data-testid={resolvedTestId}
        type="button"
        aria-current={ariaCurrent}
      >
        {content}
      </button>
    );
  }

  if (href.startsWith('#')) {
    return (
      <a
        href={href}
        className={linkClassName}
        onClick={(event) => {
          event.preventDefault();
          handleQuickLink();
        }}
        data-testid={resolvedTestId}
        aria-current={ariaCurrent}
      >
        {content}
      </a>
    );
  }

  if (isExternalUrl(href, urlTarget)) {
    return (
      <a
        href={href}
        className={linkClassName}
        target={urlTarget ?? '_blank'}
        rel="noopener noreferrer"
        data-testid={resolvedTestId}
        aria-current={ariaCurrent}
      >
        {content}
      </a>
    );
  }

  return (
    <Link
      href={ensureTrailingSlash(href)}
      prefetch
      className={linkClassName}
      data-testid={resolvedTestId}
      aria-current={ariaCurrent}
    >
      {content}
    </Link>
  );
}

NavQuickLinkPillItem.displayName = 'NavQuickLinkPillItem';
